import { useCallback, useEffect, useRef, useState } from 'react'
import { getProductAttributes, saveProductMappings } from './productAttributeApi'
import { leftSide, rightSide } from './attributeDirection'

const withRowId = (rowId, mapping) => ({ ...mapping, rowId, isNew: Boolean(mapping.isNew) })

/** A row missing a field on either side would save an empty attribute name. */
export const isRowComplete = (row, direction) =>
    leftSide(row, direction).name !== '' && rightSide(row, direction).name !== ''

/**
 * Owns one direction of the product attribute screen: the two field lists, the
 * mapping rows, and the load/save lifecycle. Re-runs whenever the direction flips,
 * since each direction reads a different schema list from the same endpoint.
 */
const useProductAttributeMapping = (connectorId, instancesId, direction) => {
    const [erpFields, setErpFields] = useState([])
    const [ecommerceFields, setEcommerceFields] = useState([])
    const [rows, setRows] = useState([])
    const [isLoading, setIsLoading] = useState(true)
    const [loadError, setLoadError] = useState('')
    const [isSaving, setIsSaving] = useState(false)
    const [saveStatus, setSaveStatus] = useState(null)
    const [isDirty, setIsDirty] = useState(false)

    const nextRowId = useRef(0)
    const isMounted = useRef(true)
    const loadedKey = useRef(null)

    useEffect(() => {
        isMounted.current = true
        return () => {
            isMounted.current = false
        }
    }, [])

    const load = useCallback(() => {
        setIsLoading(true)
        setLoadError('')

        return getProductAttributes(connectorId, instancesId, direction)
            .then(({ mappings, erpFields: erp, ecommerceFields: ecommerce }) => {
                if (!isMounted.current) {
                    return
                }
                setErpFields(erp)
                setEcommerceFields(ecommerce)
                setRows(mappings.map((mapping) => {
                    nextRowId.current += 1
                    return withRowId(nextRowId.current, mapping)
                }))
                setIsDirty(false)
                setIsLoading(false)
            })
            .catch((error) => {
                if (!isMounted.current) {
                    return
                }
                setLoadError('Unable to load the product attribute details. Please try again.')
                setIsLoading(false)
                // eslint-disable-next-line no-console
                console.error('Product attribute load failed', error)
            })
    }, [connectorId, instancesId, direction])

    useEffect(() => {
        if (!connectorId || !instancesId) {
            setIsLoading(false)
            return
        }

        // React 18 StrictMode runs effects twice in development. This ref survives
        // that simulated remount, so each connector/instance/direction is fetched
        // exactly once - no duplicate or cancelled requests in the network tab.
        const key = `${connectorId}|${instancesId}|${direction.key}`
        if (loadedKey.current === key) {
            return
        }
        loadedKey.current = key

        load()
    }, [connectorId, instancesId, direction, load])

    const addMappings = useCallback((mappings) => {
        setSaveStatus(null)
        setIsDirty(true)
        setRows((current) => {
            const added = mappings.map((mapping) => {
                nextRowId.current += 1
                return withRowId(nextRowId.current, { ...mapping, isNew: true })
            })
            return [...added, ...current]
        })
    }, [])

    const updateRow = useCallback((rowId, changes) => {
        setSaveStatus(null)
        setIsDirty(true)
        setRows((current) => current.map((row) => (row.rowId === rowId ? { ...row, ...changes } : row)))
    }, [])

    const removeRow = useCallback((rowId) => {
        setSaveStatus(null)
        setIsDirty(true)
        setRows((current) => current.filter((row) => row.rowId !== rowId))
    }, [])

    const submitMappings = useCallback(() => {
        const payload = rows.filter((row) => isRowComplete(row, direction))

        setIsSaving(true)
        setSaveStatus(null)

        return saveProductMappings(connectorId, instancesId, direction, payload)
            .then(() => {
                if (!isMounted.current) {
                    return
                }
                setRows((current) => current.map((row) => ({ ...row, isNew: false })))
                setIsDirty(false)
                setSaveStatus({
                    result: true,
                    message: `${payload.length} product attribute mapping${payload.length === 1 ? '' : 's'} saved successfully.`
                })
            })
            .catch((error) => {
                if (!isMounted.current) {
                    return
                }
                setSaveStatus({ result: false, message: 'Unable to save the product attribute mappings.' })
                // eslint-disable-next-line no-console
                console.error('Product attribute save failed', error)
            })
            .finally(() => {
                if (isMounted.current) {
                    setIsSaving(false)
                }
            })
    }, [rows, connectorId, instancesId, direction])

    const dismissStatus = useCallback(() => setSaveStatus(null), [])

    return {
        erpFields,
        ecommerceFields,
        rows,
        isLoading,
        loadError,
        isSaving,
        isDirty,
        saveStatus,
        addMappings,
        updateRow,
        removeRow,
        submitMappings,
        dismissStatus
    }
}

export default useProductAttributeMapping
